import { PropsWithChildren } from 'react';
import { AppShell as AppShellImpl, Badge, Box, Burger, Group, ScrollArea } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import type { Member } from 'stytch';
import { config } from 'util-config';

import { Navigation as HeaderNavigation } from './header-navigation';
import { Navigation as MobileNavigation } from './mobile-navigation';
import { ProfileMenu } from './profile-menu';
import { NavItem } from './types';

import HuuvaLogo from './huuva-logo.svg?react';
import * as classes from './app-shell.css';

type AppShellProps = PropsWithChildren<{
  navItems?: NavItem[];
  secondaryNavItems?: NavItem[];
  member?: Member | null;
}>;

export function AppShell({ children, navItems, secondaryNavItems, member }: AppShellProps) {
  const [opened, { toggle }] = useDisclosure();

  return (
    <AppShellImpl
      header={{ height: 55 }}
      classNames={{ root: classes.appShell, header: classes.header }}
      navbar={{ width: 300, breakpoint: 'sm', collapsed: { desktop: true, mobile: !opened } }}
    >
      <AppShellImpl.Header>
        <Group h="100%" px="lg" gap="md" justify="space-between">
          <Group gap="md" flex={1}>
            <Burger opened={opened} onClick={toggle} size="sm" hiddenFrom="sm" />
            <Title />
            {navItems && (
              <Box flex={1} visibleFrom="sm">
                <HeaderNavigation navItems={navItems} />
              </Box>
            )}
          </Group>
          <Group gap="xs">
            {secondaryNavItems && (
              <Box visibleFrom="sm">
                <HeaderNavigation navItems={secondaryNavItems} />
              </Box>
            )}
            <ProfileMenu member={member} />
          </Group>
        </Group>
      </AppShellImpl.Header>
      {/* Mobile navbar */}
      <AppShellImpl.Navbar py="md" px="xs">
        <AppShellImpl.Section grow mb="md" component={ScrollArea}>
          <MobileNavigation navItems={navItems} onLinkClick={() => toggle()} />
        </AppShellImpl.Section>
        {secondaryNavItems && (
          <AppShellImpl.Section>
            <MobileNavigation navItems={secondaryNavItems} onLinkClick={() => toggle()} />
          </AppShellImpl.Section>
        )}
      </AppShellImpl.Navbar>
      {/* Main content */}
      <AppShellImpl.Main>{children}</AppShellImpl.Main>
    </AppShellImpl>
  );
}

function Title() {
  return (
    <Group gap="xs">
      <HuuvaLogo color="#ff6b47" style={{ width: '1.25rem' }} />
      {config.APP_ENV !== 'production' && (
        <Badge color={config.APP_ENV === 'staging' ? 'grape' : 'red'}>
          {config.APP_ENV === 'staging' ? 'Stg' : 'Dev'}
        </Badge>
      )}
    </Group>
  );
}
